import dot from 'dot-prop-immutable'
import find from 'lodash/find'
import keyBy from 'lodash/keyBy'
import uniq from 'lodash/uniq'
import without from 'lodash/without'

import * as AdsATypes from 'apps/ads/constants/AdsReduxActionTypes'
import { requestConnectPageToAgency } from 'apps/agency/model/status/thunks'
import * as iceBreakersAtypes from 'apps/iceBreakers/constants/iceBreakersReduxActionTypes'
import { toggleInstagramChannel } from 'apps/instagram/store/actions/toggleChannel'
import {
  cancelEmailChange,
  deleteProfileApiToken,
  fetchAccount,
  fetchAccountFlags,
  fetchAllAccounts,
  generateProfileApiToken,
  sendEmailVerification,
  setCurrentAccountUserDisplayFlag,
  setCurrentAccountUserDisplayFlagLocaly,
  setDisplayFlag,
  setDisplayFlagLocaly,
  setProfileTimezone,
  setSkipPreviewOnboardingModal,
  verifyEmailViaCode,
} from 'apps/profile/actions/profileActions'
import * as smsAtypes from 'apps/sms/smsReduxActionTypes'
import { reducer as telegramReducer } from 'apps/telegram/redux/telegramSlice'
import * as tiktokAtypes from 'apps/tiktok/constants/tiktokReduxActionTypes'
import {
  initialState as waChannelInitialState,
  reducer as waChannelReducer,
} from 'apps/whatsApp/redux/waChannelSlice'
import { disconnectIntegration } from 'common/actions/integrations/common/actions/integrationActions'
import {
  backToPro,
  cancelSubscription,
  cancelSubscriptionWithRefund,
  fetchPro,
  proStatusUpdate,
} from 'common/billing/actions/billingActions'
import { migratePaidToV3 } from 'common/billing/actions/upgradeActions'
import {
  fetchExtendedAccountData,
  getListSuspectedFbPolicyOffenders,
  readAllPolicyEnforcementNotifications,
  startVersionChecker,
  updateConnectedIntegrations,
  updateWebPushConsent,
} from 'common/core/actions/appActions'
import * as appAtypes from 'common/core/constants/appReduxActionTypes'
import { AccountStatus } from 'common/core/models/Account/constants'
import { reducer as userActivityReducer } from 'common/core/reducers/confirmUserActivitySlice'
import { setFusion } from 'common/fusion/actions'
import {
  fetchPricingLimitInfo,
  getMessagingFeatureReview,
  setBotTimezone,
  setCurrency,
  setPageName,
  updateSettingsState,
} from 'common/settings/actions'
import * as personaActionsTypes from 'common/settings/actions/personaActionsTyped'
import { reducer as installedApplicationsReducer } from 'common/settings/components/SettingsPanel/AppSection/installedApplications/reducers/installedApplications'
import * as settingsActionTypes from 'common/settings/SettingsReduxActionTypes'
import { UploadAvatarType } from 'shared/api/requests/settings/schemas/SetPersonaApiDataSchema'
import { parseInitApp } from 'utils/parseInitApp'

import { fetchPages } from '../actions/pagesActions'
import { ChannelType } from '../constants/ChannelType'

import { abilitiesSlice } from './abilities'

const InitialCurrentAccountState = {
  installed_applications: [],
  is_ig_summer_promo_activated: false,
  fb_channel: {},
  instagram_channel: {},
  sms_channel: {},
  email_channel: {},
  products: {
    has_inbox: false,
    has_automation: false,
  },
  abilities: abilitiesSlice.getInitialState(),
  whatsapp_channel: waChannelInitialState,
}

export const initialState = {
  currentAccount: InitialCurrentAccountState,
  actualAppVersion: '',
  isFetchingCheckVersion: false,
  notifications: [],
  closedNotificationIds: [],
  accountsCount: 0,
  activeTab: null,
  pages: null,
  title: '',
  optIn: {
    subscriberId: null,
    [ChannelType.FB]: {
      optInRequired: false,
      payload: null,
      updateLastInteractionRequired: false,
    },
    [ChannelType.SMS]: {
      optInRequired: false,
      errors: [],
      subscriberErrors: [], // ISubscriberError[]
    },
    [ChannelType.EMAIL]: {
      optInRequired: false,
      errors: [],
      subscriberErrors: [], // ISubscriberError[]
      defaultEmail: '',
    },
    [ChannelType.WHATSAPP]: {
      optInRequired: false,
      link: null,
      updateLastInteractionRequired: false,
    },
  },
  fbsdk: {
    loaded: false,
    locale: null,
  },
  userID: null,
  user: {
    isVerifyingEmail: false,
  },
}

export default function appReducer(state = initialState, action) {
  const abilities = abilitiesSlice.reducer(state.currentAccount.abilities, action)
  if (abilities !== state.currentAccount.abilities) {
    state = {
      ...state,
      currentAccount: {
        ...state.currentAccount,
        abilities,
      },
    }
  }

  const installed_applications = installedApplicationsReducer(
    state.currentAccount.installed_applications,
    action,
  )
  if (state.currentAccount.installed_applications !== installed_applications) {
    state = {
      ...state,
      currentAccount: {
        ...state.currentAccount,
        installed_applications,
      },
    }
  }

  switch (action.type) {
    case 'SET_PRO_STATUS_BLOCKED': {
      const { subscribers_total, reasons, limits } = action.payload

      return dot.merge(state, 'currentAccount', {
        is_blocked: true,
        subscribers_total,
        block_reasons: reasons,
        block_limits: limits,
      })
    }

    case cancelSubscriptionWithRefund.fulfilled.type: {
      return dot.merge(state, 'currentAccount', {
        is_blocked: false,
        pro_status: AccountStatus.EXPIRED2020,
      })
    }

    case setFusion.fulfilled.type: {
      return dot.merge(state, 'currentAccount.fusion', {
        ...action.payload,
        show_promotion: false,
      })
    }

    case migratePaidToV3.fulfilled.type: {
      const billingInfo = dot.get(state, 'currentAccount.billing_info')

      return dot.merge(state, 'currentAccount', {
        billing_info: {
          ...billingInfo,
          billing_version: action.payload.data.billing_version,
        },
      })
    }

    case 'SET_PRO_STATUS_UNBLOCKED': {
      return dot.merge(state, 'currentAccount', {
        is_blocked: false,
        pro_status: action.payload || AccountStatus.FREE2020,
      })
    }

    case appAtypes.APP_UPDATE: {
      if (!action?.data) {
        return { ...state }
      }

      const app = parseInitApp(action.data)

      return { ...state, ...app }
    }

    case appAtypes.APP_UPDATE_CURRENT_ACCOUNT_ID: {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          id: action.accountId,
        },
      }
    }

    case appAtypes.APP_UPDATE_CURRENT_ACCOUNT: {
      return dot.merge(state, 'currentAccount', action.changes)
    }

    case appAtypes.APP_RESET_CURRENT_ACCOUNT: {
      return {
        ...state,
        currentAccount: InitialCurrentAccountState,
      }
    }

    case fetchExtendedAccountData.fulfilled.type: {
      const updatedCurrentAccount = action?.payload?.data

      if (!updatedCurrentAccount) return state

      return dot.merge(state, 'currentAccount', {
        ...updatedCurrentAccount,
        isExtendedDataLoaded: true,
      })
    }

    case appAtypes.APP_UPDATE_THREADS_STATS: {
      return dot.merge(state, 'currentAccount.threads_statuses', action.data)
    }

    case appAtypes.APP_APPLY_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.FB}`, {
        optInRequired: true,
        payload: action.payload,
        updateLastInteractionRequired: Boolean(action.updateLastInteractionRequired),
      })
    }

    case appAtypes.APP_SET_LAST_PREVIEW_BUILDER_ID: {
      return dot.merge(state, `optIn.${ChannelType.WHATSAPP}`, {
        lastPreviewBuilderId: action.builderId,
      })
    }

    case appAtypes.APP_SET_WHATSAPP_OPTIN_MODAL_OPEN: {
      return dot.merge(state, `optIn.${ChannelType.WHATSAPP}`, {
        isModalOpen: action.isOpen,
      })
    }

    case appAtypes.APP_DROP_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.FB}`, {
        optInRequired: false,
        payload: null,
        updateLastInteractionRequired: false,
      })
    }

    case appAtypes.APP_APPLY_SMS_EMAIL_OPTIN_REQUIRED: {
      state = dot.merge(state, `optIn`, {
        subscriberId: action.subscriberId,
      })

      state = dot.merge(state, `optIn.${ChannelType.SMS}`, {
        optInRequired: Boolean(action.smsOptInRequired),
      })

      state = dot.merge(state, `optIn.${ChannelType.EMAIL}`, {
        optInRequired: Boolean(action.emailOptInRequired),
        defaultEmail: action.defaultEmail || '',
      })

      return state
    }

    case appAtypes.APP_DROP_SMS_EMAIL_OPTIN_REQUIRED: {
      state = dot.merge(state, `optIn`, {
        subscriberId: null,
      })

      state = dot.merge(state, `optIn.${ChannelType.SMS}`, {
        optInRequired: false,
      })
      state = dot.merge(state, `optIn.${ChannelType.EMAIL}`, {
        optInRequired: false,
        defaultEmail: '',
      })

      return state
    }

    case appAtypes.APP_SET_SMS_EMAIL_OPTIN_ERRORS: {
      if (action.subscriberErrors) {
        const ChannelTypeByErrorType = {
          phone_number: ChannelType.SMS,
          email: ChannelType.EMAIL,
        }

        const subscriberErrorsByChannel = { [ChannelType.SMS]: [], [ChannelType.EMAIL]: [] }

        action.subscriberErrors.forEach((error) => {
          subscriberErrorsByChannel[ChannelTypeByErrorType[error.type]].push({
            subscriberId: error.id,
            channel: ChannelTypeByErrorType[error.type],
          })
        })

        // If a channel doesn't has subscriberErrors, it will be reset, it's ok
        state = dot.merge(state, `optIn.${ChannelType.EMAIL}`, {
          subscriberErrors: subscriberErrorsByChannel[ChannelType.EMAIL],
        })
        state = dot.merge(state, `optIn.${ChannelType.SMS}`, {
          subscriberErrors: subscriberErrorsByChannel[ChannelType.SMS],
        })
      }

      if (action.smsErrors) {
        state = dot.merge(state, `optIn.${ChannelType.SMS}`, {
          errors: action.smsErrors,
        })
      }
      if (action.emailErrors) {
        state = dot.merge(state, `optIn.${ChannelType.EMAIL}`, {
          errors: action.emailErrors,
        })
      }

      return state
    }

    case appAtypes.APP_DROP_SMS_EMAIL_OPTIN_ERRORS: {
      state = dot.merge(state, `optIn.${ChannelType.SMS}`, {
        errors: [],
        subscriberErrors: [],
      })
      state = dot.merge(state, `optIn.${ChannelType.EMAIL}`, {
        errors: [],
        subscriberErrors: [],
      })

      return state
    }

    case appAtypes.APP_APPLY_WHATSAPP_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.WHATSAPP}`, {
        optInRequired: true,
        link: action.link,
        updateLastInteractionRequired: Boolean(action.updateLastInteractionRequired),
      })
    }

    case appAtypes.APP_DROP_WHATSAPP_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.WHATSAPP}`, {
        optInRequired: false,
        link: null,
        updateLastInteractionRequired: false,
      })
    }

    case appAtypes.APP_APPLY_TELEGRAM_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.TELEGRAM}`, {
        optInRequired: true,
        link: action.link,
        botName: action.botName,
      })
    }

    case appAtypes.APP_DROP_TELEGRAM_OPTIN_REQUIRED: {
      return dot.merge(state, `optIn.${ChannelType.TELEGRAM}`, {
        optInRequired: false,
        link: null,
        botName: null,
      })
    }

    case appAtypes.APP_SET_ACTIVE_TAB: {
      return { ...state, activeTab: action.tab }
    }

    case appAtypes.APP_SET_TITLE: {
      return { ...state, title: action.title }
    }

    case appAtypes.APP_LOAD_FBSDK_SUCCESS: {
      return { ...state, fbsdk: { loaded: true, locale: action.locale } }
    }

    case setBotTimezone.fulfilled.type: {
      if (action.meta?.arg?.timezone) {
        return dot.set(state, 'currentAccount.timezone', action.meta.arg.timezone)
      }

      return state
    }

    case setPageName.fulfilled.type: {
      return dot.set(state, 'currentAccount.title', action.meta.arg)
    }

    case appAtypes.APP_LOAD_FBSDK_REQUEST: {
      return { ...state, fbsdk: initialState.fbsdk }
    }

    case startVersionChecker.pending.type: {
      return { ...state, isFetchingCheckVersion: true }
    }

    case startVersionChecker.rejected.type: {
      return { ...state, isFetchingCheckVersion: false }
    }

    case startVersionChecker.fulfilled.type: {
      const { payload } = action
      const hasKeys = Object.keys(payload).length > 0

      if (!hasKeys) {
        return { ...state, isFetchingCheckVersion: false }
      }

      const actualAppVersion = Object.values(payload).reduce((first, cur) => first + cur)
      return { ...state, actualAppVersion, isFetchingCheckVersion: false }
    }

    case settingsActionTypes.SETTINGS_NOTIFICATION_REMOVE_REQUEST: {
      const { notification_id } = action
      return {
        ...state,
        notifications: without(
          state.notifications,
          find(
            state.notifications,
            (notification) => String(notification.notification_id) === String(notification_id),
          ),
        ),
      }
    }

    case settingsActionTypes.SETTINGS_NOTIFICATION_TEMPORARY_REMOVE: {
      return {
        ...state,
        closedNotificationIds: uniq([...state.closedNotificationIds, action.notification_id]),
      }
    }

    case settingsActionTypes.SETTINGS_NOTIFICATION_REMOVE_FROM_CLOSED_LIST: {
      const { notificationId } = action
      const resultArrayNotifications = state.closedNotificationIds.filter(
        (el) => notificationId !== el,
      )

      return {
        ...state,
        closedNotificationIds: resultArrayNotifications,
      }
    }

    case settingsActionTypes.SETTINGS_NOTIFICATIONS_SET: {
      return { ...state, notifications: action.payload }
    }

    case appAtypes.READ_POLICY_ENFORCEMENT_NOTIFICATION_NOTIFICATION:
    case readAllPolicyEnforcementNotifications.fulfilled.type: {
      return dot.set(state, 'currentAccountUser.fbPolicyEnforcementNotifications', [])
    }

    case appAtypes.NEW_POLICY_ENFORCEMENT_NOTIFICATION_NOTIFICATION: {
      return dot.merge(state, 'currentAccountUser.fbPolicyEnforcementNotifications', [action.item])
    }

    case getListSuspectedFbPolicyOffenders.pending.type: {
      return dot.merge(state, 'currentAccount.suspectedFbPolicyOffenders', { fetching: true })
    }

    case getListSuspectedFbPolicyOffenders.fulfilled.type: {
      return dot.merge(state, 'currentAccount.suspectedFbPolicyOffenders', {
        fetching: false,
        data: action.payload.list,
      })
    }

    case getListSuspectedFbPolicyOffenders.rejected.type: {
      return dot.merge(state, 'currentAccountUser.suspectedFbPolicyOffenders', { fetching: false })
    }

    case updateSettingsState.type: {
      return { ...state, paymentCurrency: action.payload.settings.payments.currency }
    }

    case getMessagingFeatureReview.fulfilled.type: {
      const features = keyBy(action.payload, 'feature_name')
      state = dot.set(
        state,
        'currentAccount.subscription_permission_status',
        features.subscription_messaging?.status,
      )
      state = dot.set(
        state,
        'currentAccount.one_time_notify_status',
        features.one_time_notif?.status,
      )
      return state
    }

    case settingsActionTypes.SETTINGS_DEVLOGS_UPDATE_LAST_READ: {
      return dot.set(state, 'currentAccount.last_devlog_critical_message_ts', action.data.timestamp)
    }

    case settingsActionTypes.SETTINGS_DEVLOGS_MARK_AS_READ_RESPONSE: {
      return dot.merge(state, 'currentAccountUser', action.data.accountUser)
    }

    case disconnectIntegration.fulfilled.type:
    case updateConnectedIntegrations.type: {
      return dot.set(
        state,
        'currentAccount.connected_integrations',
        action.payload.connected_integrations,
      )
    }

    case smsAtypes.SMS_ENABLE_CHANNEL_RESPONSE: {
      return dot.set(state, 'currentAccount.sms_channel.sms_channel_connected', true)
    }
    case smsAtypes.SMS_DISABLE_CHANNEL_RESPONSE: {
      return dot.set(state, 'currentAccount.sms_channel.sms_channel_connected', false)
    }

    case sendEmailVerification.pending.type: {
      return { ...state, user: { ...state.user, isVerifyingEmail: true } }
    }

    case sendEmailVerification.fulfilled.type: {
      const data = action.payload
      return { ...state, user: { ...state.user, ...data, isVerifyingEmail: false } }
    }

    case sendEmailVerification.rejected.type: {
      return { ...state, user: { ...state.user, isVerifyingEmail: false } }
    }

    case verifyEmailViaCode.fulfilled.type: {
      const data = action.payload
      return { ...state, user: { ...state.user, ...data } }
    }

    case cancelEmailChange.fulfilled.type: {
      const data = action.payload
      return { ...state, user: { ...state.user, ...data } }
    }

    case AdsATypes.ADS_PAGE_ENABLE_RESPONSE: {
      return { ...state, currentAccount: { ...state.currentAccount, is_ads_enabled: true } }
    }

    case requestConnectPageToAgency.fulfilled.type: {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          agency_connect_active: true,
        },
      }
    }

    case setProfileTimezone.fulfilled.type: {
      const { timezone } = action.payload
      return dot.set(state, 'user.timezone', timezone)
    }

    case updateWebPushConsent.fulfilled.type: {
      const { payload } = action

      return dot.set(state, 'user.web_push_consent', payload)
    }

    case setDisplayFlag.fulfilled.type:
    case setDisplayFlagLocaly.type: {
      const { displayFlag, value } = action.payload

      if (state.user?.interface_data?.display_flags) {
        return dot.merge(state, 'user.interface_data.display_flags', {
          [displayFlag]: value,
        })
      }

      return dot.merge(state, 'user.interface_data', {
        display_flags: {
          [displayFlag]: value,
        },
      })
    }

    case setCurrentAccountUserDisplayFlagLocaly.type:
    case setCurrentAccountUserDisplayFlag.fulfilled.type: {
      const { displayFlag, value } = action.payload

      if (state.currentAccountUser?.interface_data?.display_flags) {
        return dot.merge(state, 'currentAccountUser.interface_data.display_flags', {
          [displayFlag]: value,
        })
      }

      return dot.merge(state, 'currentAccountUser.interface_data', {
        display_flags: {
          [displayFlag]: value,
        },
      })
    }

    case fetchAccountFlags.fulfilled.type: {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          flags: {
            ...action.payload,
          },
        },
      }
    }

    case settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_ROLE: {
      return dot.set(state, 'currentAccountUser.user_role', action.role)
    }
    case settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_BILLING_ACCESS: {
      return dot.set(state, 'currentAccountUser.has_billing_access', action.hasBillingAccess)
    }
    case generateProfileApiToken.fulfilled.type: {
      return dot.set(state, 'user.user_api_token', action.payload.token)
    }
    case deleteProfileApiToken.fulfilled.type: {
      return dot.set(state, 'user.user_api_token', '')
    }

    case fetchAccount.fulfilled.type: {
      const hasAlreadyFetched = state.accounts.some((acc) => acc.id === action.payload.id)
      return hasAlreadyFetched ? state : { ...state, accounts: [...state.accounts, action.payload] }
    }
    case fetchAllAccounts.fulfilled.type: {
      return { ...state, accounts: action.payload }
    }

    case appAtypes.FETCH_PREVIEW_REF_URL_RESPONSE: {
      return dot.merge(state, `optIn.${ChannelType.FB}`, {
        previewRefURL: action.data.preview_ref,
      })
    }

    case setSkipPreviewOnboardingModal.fulfilled.type: {
      return {
        ...state,
        user: { ...state.user, skip_preview_onboarding_modal: action.meta.arg },
      }
    }

    case appAtypes.CURRENT_ACCOUNT_UPDATE_ASYNC_BILLING_ACTIONS: {
      const { status, url } = action

      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          billing_next_action_required: status
            ? {
                redirect_to_url: {
                  next_action_redirect: url,
                  action_type:
                    state.currentAccount.billing_next_action_required?.redirect_to_url?.action_type,
                },
              }
            : null,
        },
      }
    }

    // Ice Breakers
    case iceBreakersAtypes.ICE_BREAKERS_SWITCH_ENABLED_NOTIFICATION:
    case iceBreakersAtypes.ICE_BREAKERS_SWITCH_ENABLED_RESPONSE: {
      const iceBreakers = action.data.ice_breakers || []
      const { channel } = action
      // Not sure why we need to check the channel twice
      const hasInstagramIceBreakers = iceBreakers.some((ib) => ib.channel === ChannelType.INSTAGRAM)
      const hasWhatsAppIceBreakers = iceBreakers.some((ib) => ib.channel === ChannelType.WHATSAPP)
      if (hasInstagramIceBreakers || channel === ChannelType.INSTAGRAM) {
        return dot.set(
          state,
          'currentAccount.instagram_channel.icebreakers_instagram_enabled',
          action.data.is_available,
        )
      } else if (hasWhatsAppIceBreakers || channel === ChannelType.WHATSAPP) {
        return dot.set(
          state,
          'currentAccount.whatsapp_channel.icebreakers_whatsapp_enabled',
          action.data.is_available,
        )
      }

      return dot.set(
        state,
        'currentAccount.fb_channel.icebreakers_enabled',
        action.data.is_available,
      )
    }

    case appAtypes.CURRENT_ACCOUNT_UPDATE_WHATSAPP_CHANNEL: {
      return dot.set(state, 'currentAccount.whatsapp_channel', action.data)
    }

    case fetchPricingLimitInfo.fulfilled.type: {
      if (!action.payload) {
        return state
      }

      return dot.merge(state, 'currentAccount', action.payload)
    }

    case toggleInstagramChannel.fulfilled.type:
      return dot.set(state, 'currentAccount.instagram_channel.is_channel_enabled', action.payload)

    case tiktokAtypes.TOGGLE_TIKTOK_CHANNEL_RESPONSE:
      return dot.set(state, 'currentAccount.tiktok_channel.is_channel_enabled', action.value)

    case cancelSubscription.fulfilled.type: {
      if (state.currentAccount.instagram_channel) {
        state = dot.set(
          state,
          'currentAccount.instagram_channel.is_ig_summer_promo_activated',
          false,
        )
      }
      return dot.set(state, 'currentAccount.is_ig_summer_promo_activated', false)
    }

    case appAtypes.ACCOUNT_SET_DISPLAY_FLAG_RESPONSE: {
      const { display_flag, value } = action

      if (state.currentAccount?.interface_data?.display_flags) {
        return dot.merge(state, 'currentAccount.interface_data.display_flags', {
          [display_flag]: value,
        })
      }

      return dot.merge(state, 'currentAccount.interface_data', {
        display_flags: {
          [display_flag]: value,
        },
      })
    }

    case proStatusUpdate.type: {
      return dot.set(state, 'currentAccount.pro_status', action.payload)
    }

    case fetchPro.fulfilled.type: {
      const billingInfo = dot.get(state, 'currentAccount.billing_info')

      return dot.merge(state, 'currentAccount', {
        pro_status: action.payload?.pro.status,
        trial_unavailable_reason: action.payload?.pro.trial_unavailable_reason,
        billing_info: {
          ...billingInfo,
          billing_version: action.payload?.pro.billing_version,
        },
      })
    }

    case backToPro.fulfilled.type: {
      const billingInfo = dot.get(state, 'currentAccount.billing_info')

      return dot.merge(state, 'currentAccount', {
        pro_status: action.payload?.pro.status,
        billing_info: {
          ...billingInfo,
          billing_version: action.payload?.pro.billing_version,
        },
      })
    }

    case setCurrency.fulfilled.type: {
      const currency = action.payload.settings.payments.currency

      return dot.set(state, 'paymentCurrency', currency)
    }

    case personaActionsTypes.setPersonaApiData.fulfilled.type: {
      return dot.set(state, 'currentAccountUser.persona_api', action.payload.persona_api)
    }

    case personaActionsTypes.uploadMultiple.fulfilled.type: {
      const attachments = action.payload.attachments || {}
      const personaAPI = dot.get(state, 'currentAccountUser.persona_api', {})

      if (!personaAPI.persona_api_avatar_data) {
        personaAPI.persona_api_avatar_data = {}
      }

      Object.keys(attachments).forEach((key) => {
        if (key === UploadAvatarType.SOURCE) {
          personaAPI.persona_api_avatar_data.original_avatar_url = attachments[key].img_big
        }
        if (key === UploadAvatarType.CROPPED) {
          personaAPI.persona_api_avatar_data.resize_avatar_url = attachments[key].img_big
        }
      })

      return {
        ...state,
        currentAccountUser: {
          ...state.currentAccountUser,
          persona_api: personaAPI,
        },
      }
    }
    case fetchPages.fulfilled.type: {
      return {
        ...state,
        pages: action.payload,
      }
    }
  }

  state = whatsAppChannelReducer(state, action)
  state = telegramChannelReducer(state, action)
  state = confirmUserActivityReducer(state, action)

  return state
}

const whatsAppChannelReducer = (state, action) => {
  const result = waChannelReducer(state.currentAccount.whatsapp_channel, action)
  if (result === state.currentAccount.whatsapp_channel) {
    return state
  }

  return {
    ...state,
    currentAccount: {
      ...state.currentAccount,
      whatsapp_channel: waChannelReducer(state.currentAccount.whatsapp_channel, action),
    },
  }
}

const telegramChannelReducer = (state, action) => {
  const result = telegramReducer(state.currentAccount.telegram_channel, action)
  if (result === state.currentAccount.telegram_channel) {
    return state
  }

  return {
    ...state,
    currentAccount: {
      ...state.currentAccount,
      telegram_channel: result,
    },
  }
}

const confirmUserActivityReducer = (state, action) => {
  const result = userActivityReducer(state.user.activity, action)
  if (result === state.user.activity) {
    return state
  }

  return {
    ...state,
    user: {
      ...state.user,
      activity: userActivityReducer(state.user.activity, action),
    },
  }
}
